// Aerial perspective inside the air that agrees with the sky dome (sky.js), so
// the far ground and sea fade into the horizon sky instead of meeting it in a
// line.
//
// What a real horizon looks like from a few kilometres up: the ground's light
// reaches the eye through a long, low path in the boundary layer, so it comes
// in as L0 T plus the light the path itself scatters, and toward the horizon T
// falls to almost nothing. The sea and the sky then meet at nearly the same
// brightness and colour, and the boundary all but disappears in a pale band.
// The dome already computes exactly that for its own ground rays, but the
// terrain and the sea draw over it opaquely with their own haze, which was a
// single grey exponential (180 km visibility, 2 km scale height) whose colour
// was a CPU guess at the low sky: the far sea stopped at a slate grey and met
// the dome's whitish horizon band in a hard line.
//
// So inside the air, on Earth, the surfaces that reach the horizon (the
// terrain and the near sea) take:
//   - the dome's own extinction along the view ray: Rayleigh per channel
//     (5.8, 13.5, 33.1 per Mm, 8 km scale height) and Mie (16 per Mm times
//     1.1, 1.2 km scale height), through the curved air (airdepth.js), so
//     they fade the way the dome's ground rays do;
//   - plus the marine boundary layer the dome leaves out: its Mie term is air
//     with about 200 km visibility, where the sea air over the Cape is nearer
//     50 to 60 km, a grey aerosol about a kilometre deep (40 per Mm at the
//     surface, 1 km scale height). It only hastens the fade below the horizon
//     (the colour it fades to is still the dome's), so the far sea softens into
//     the band over a degree or two from low altitude, and from above it the
//     layer's top shows as the soft haze band real photographs have;
//   - a path colour that is the dome's own radiance just above the horizon in
//     that direction. A 64 x 1 strip is rendered each frame with the dome's
//     shader (its sun disc left out), across the azimuth from the Sun's side
//     (0) to the far side (1): the sky is symmetric about the Sun's vertical,
//     so half a turn is enough. Where the path is opaque the ground becomes
//     that radiance, which is the dome's own colour beside it.
// Above 20 km this hands back to the haze and the veil that were tuned for
// the view from orbit (gone by 40 km), so that look is unchanged.
import * as THREE from 'three';

const STRIP_W = 64;
// How far above the geometric horizon the strip looks (rad). A ray that
// grazes the horizon by this much passes a few hundred metres above the sea
// at its tangent point, which is the radiance the ground beside it meets.
const STRIP_LIFT = 0.0009;
// Altitudes (m) over which this hands back to the orbit-tuned haze and veil.
const AERIAL_FULL = 20000, AERIAL_OFF = 40000;

export const AERIAL = {
  rkHorizon: { value: null },
  rkUpW: { value: new THREE.Vector3(0, 1, 0) },
  rkSunHW: { value: new THREE.Vector3(1, 0, 0) },
  rkAerialOn: { value: 0 }
};

// Needs airCurvedDepth (airdepth.js) declared before it. rayW is the view ray
// in the local scene's (inertial) axes, len its length in metres, camH the
// camera's altitude. Returns the transmittance and the path colour the dome
// would put between the camera and that point.
export const AERIAL_GLSL = /* glsl */`
uniform sampler2D rkHorizon;
uniform vec3 rkUpW;
uniform vec3 rkSunHW;
uniform float rkAerialOn;
void rkAerialTL(vec3 rayW, float len, float camH, out vec3 rkT, out vec3 rkL) {
  vec3 dir = rayW / max(len, 1e-3);
  float mu = dot(dir, rkUpW);
  float odR = airCurvedDepth(camH, mu, len, 8000.0);
  float odM = airCurvedDepth(camH, mu, len, 1200.0);
  float odB = airCurvedDepth(camH, mu, len, 1000.0);
  rkT = exp(-(vec3(5.8e-6, 13.5e-6, 33.1e-6) * odR + vec3(1.76e-5 * odM + 4.0e-5 * odB)));
  vec3 h = dir - rkUpW * mu;
  float hl = length(h);
  float az = hl > 1e-5 ? acos(clamp(dot(h / hl, rkSunHW), -1.0, 1.0)) * 0.31830989 : 0.5;
  rkL = texture(rkHorizon, vec2((0.5 + az * ${(STRIP_W - 1).toFixed(1)}) / ${STRIP_W.toFixed(1)}, 0.5)).rgb;
}
`;

// The dome's fragment shader, pointed at the strip: each pixel is a direction
// at the strip's elevation, at its azimuth from the Sun's side.
function stripFragment(domeFrag) {
  const decl = 'varying vec3 vDir;';
  const use = 'vec3 dir = normalize(vDir);';
  if (!domeFrag.includes(decl) || !domeFrag.includes(use)) return null;
  return domeFrag
    .replace(decl, `varying vec2 vStripUv;
  uniform vec3 uStripUp;
  uniform vec3 uStripSun;
  uniform vec3 uStripSide;
  uniform float uStripElev;`)
    .replace(use, `float stripAz = vStripUv.x * 3.14159265;
    vec3 dir = normalize(cos(uStripElev) * (cos(stripAz) * uStripSun + sin(stripAz) * uStripSide) + sin(uStripElev) * uStripUp);`);
}

const STRIP_VERT = /* glsl */`
  varying vec2 vStripUv;
  void main() {
    vStripUv = uv;
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`;

export function createAerial(renderer, sky) {
  const frag = stripFragment(sky.mesh.material.fragmentShader);
  if (!frag) {
    console.error('[rocketry] aerial: the sky dome shader changed shape; the horizon strip is off');
    return { update() { AERIAL.rkAerialOn.value = 0; }, dispose() {} };
  }
  const target = new THREE.WebGLRenderTarget(STRIP_W, 1, {
    type: THREE.HalfFloatType, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter,
    depthBuffer: false, generateMipmaps: false
  });
  target.texture.colorSpace = THREE.NoColorSpace;
  target.texture.wrapS = target.texture.wrapT = THREE.ClampToEdgeWrapping;
  AERIAL.rkHorizon.value = target.texture;
  const stripUniforms = {
    uStripUp: { value: new THREE.Vector3(0, 1, 0) },
    uStripSun: { value: new THREE.Vector3(1, 0, 0) },
    uStripSide: { value: new THREE.Vector3(0, 0, 1) },
    uStripElev: { value: 0 }
  };
  const material = new THREE.ShaderMaterial({
    vertexShader: STRIP_VERT,
    fragmentShader: frag,
    // The dome's own uniform objects, so the strip sees exactly what the dome
    // draws this frame.
    uniforms: { ...sky.uniforms, ...stripUniforms },
    depthTest: false, depthWrite: false, blending: THREE.NoBlending
  });
  const scene = new THREE.Scene();
  const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material);
  quad.frustumCulled = false;
  scene.add(quad);
  const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  const up = new THREE.Vector3(), sunH = new THREE.Vector3(), side = new THREE.Vector3();

  // Once a frame, after sky.update(). camInertial: the camera from the body's
  // centre (m); sunDir: unit vector to the Sun, in the same axes.
  function update(body, camInertial, sunDir, camAlt) {
    // window.__rkAerialOff = true turns this off, for before/after captures.
    const on = typeof window !== 'undefined' && window.__rkAerialOff ? 0 : body && body.key === 'earth' && body.atmosphereHeight && camAlt < body.atmosphereHeight && sky.mesh.visible
      ? 1 - THREE.MathUtils.smoothstep(camAlt, AERIAL_FULL, AERIAL_OFF) : 0;
    AERIAL.rkAerialOn.value = on;
    if (on <= 0) return;
    const r = Math.hypot(camInertial.x, camInertial.y, camInertial.z) || 1;
    up.set(camInertial.x / r, camInertial.y / r, camInertial.z / r);
    sunH.copy(sunDir).addScaledVector(up, -sunDir.dot(up));
    if (sunH.lengthSq() < 1e-10) sunH.set(1, 0, 0).addScaledVector(up, -up.x);
    sunH.normalize();
    side.crossVectors(up, sunH).normalize();
    AERIAL.rkUpW.value.copy(up);
    AERIAL.rkSunHW.value.copy(sunH);
    stripUniforms.uStripUp.value.copy(up);
    stripUniforms.uStripSun.value.copy(sunH);
    stripUniforms.uStripSide.value.copy(side);
    const R = body.radius;
    const dip = Math.acos(Math.min(1, R / r));
    stripUniforms.uStripElev.value = -dip + STRIP_LIFT;
    // Leave out the sun's disc (a strip pixel next to a low sun would carry
    // its whole radiance into the haze), and draw at unit exposure.
    const disc = sky.uniforms.uSunDisc.value, exposure = sky.uniforms.uExposure.value;
    sky.uniforms.uSunDisc.value = 0;
    sky.uniforms.uExposure.value = 1;
    const prevTarget = renderer.getRenderTarget();
    const prevAutoClear = renderer.autoClear;
    renderer.autoClear = false;
    renderer.setRenderTarget(target);
    renderer.render(scene, camera);
    renderer.setRenderTarget(prevTarget);
    renderer.autoClear = prevAutoClear;
    sky.uniforms.uSunDisc.value = disc;
    sky.uniforms.uExposure.value = exposure;
  }

  function dispose() {
    target.dispose();
    material.dispose();
    quad.geometry.dispose();
    if (AERIAL.rkHorizon.value === target.texture) AERIAL.rkHorizon.value = null;
  }

  return { update, dispose, target };
}
